import React, { useEffect, useState } from 'react';
import Button from '../common/Button.js';
import FormInput from '../common/FormInput.js';
import SearchableSelectField from '../common/SearchableSelectField.js';
import Pagination from '../common/Pagination.js';
import Modal from '../common/Modal.js';
import {
  createExpense,
  updateExpense,
  deleteExpense,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory
} from '../../services/expenseApi.js';
import useExpenseStore from '../../store/branchstore/BranchExpenseStore.js';

const Expenses = () => {
  const {
    expenses,
    summary,
    categories,
    loading,
    error,
    currentPage,
    pageSize,
    loadExpenses,
    setCurrentPage,
    setPageSize,
    addExpense,
    updateExpense: updateExpenseInStore,
    removeExpense,
    addCategory,
    updateCategory,
    removeCategory
  } = useExpenseStore((state) => ({
    expenses: state.expenses,
    summary: state.summary,
    categories: state.categories,
    loading: state.loading,
    error: state.error,
    currentPage: state.currentPage,
    pageSize: state.pageSize,
    loadExpenses: state.loadExpenses,
    setCurrentPage: state.setCurrentPage,
    setPageSize: state.setPageSize,
    addExpense: state.addExpense,
    updateExpense: state.updateExpense,
    removeExpense: state.removeExpense,
    addCategory: state.addCategory,
    updateCategory: state.updateCategory,
    removeCategory: state.removeCategory
  }));

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const [message, setMessage] = useState('');
  const [storeError, setStoreError] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryMessage, setCategoryMessage] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    category_id: '',
    expense_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Load expenses once on component mount
  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewCategoryChange = (e) => {
    setNewCategoryName(e.target.value);
    setCategoryError('');
    setCategoryMessage('');
  };

  const handleEditCategoryStart = (category) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
    setCategoryError('');
    setCategoryMessage('');
  };

  const handleCategoryNameChange = (e) => {
    setEditingCategoryName(e.target.value);
  };

  const handleCancelCategoryEdit = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setCategoryError('Enter a category name.');
      return;
    }

    try {
      const response = await createExpenseCategory({ name: newCategoryName.trim() });
      const category = response.category || response;
      addCategory(category);
      setCategoryMessage(`Category "${category.name}" added.`);
      setNewCategoryName('');
      setTimeout(() => setCategoryMessage(''), 3000);
    } catch (err) {
      setCategoryError(err.message || 'Failed to add category');
    }
  };

  const handleSaveCategoryEdit = async () => {
    if (!editingCategoryName.trim()) {
      setCategoryError('Category name cannot be empty.');
      return;
    }

    try {
      const response = await updateExpenseCategory(editingCategoryId, {
        name: editingCategoryName.trim()
      });
      const updatedCategory = response.category || response;
      updateCategory(editingCategoryId, updatedCategory);
      setCategoryMessage('Category updated successfully.');
      setEditingCategoryId(null);
      setEditingCategoryName('');
      setTimeout(() => setCategoryMessage(''), 3000);
    } catch (err) {
      setCategoryError(err.message || 'Failed to update category');
    }
  };

  const handleToggleCategoryActive = async (category) => {
    try {
      const response = await updateExpenseCategory(category.id, {
        name: category.name,
        is_active: !category.is_active
      });
      const updatedCategory = response.category || response;
      updateCategory(category.id, updatedCategory);
      setCategoryMessage(`Category ${updatedCategory.is_active ? 'activated' : 'deactivated'}.`);
      setTimeout(() => setCategoryMessage(''), 3000);
    } catch (err) {
      setCategoryError(err.message || 'Failed to update category status');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? It cannot be restored.')) {
      return;
    }

    try {
      await deleteExpenseCategory(categoryId);
      removeCategory(categoryId);
      setCategoryMessage('Category deleted successfully.');
      setTimeout(() => setCategoryMessage(''), 3000);
    } catch (err) {
      setCategoryError(err.message || 'Failed to delete category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStoreError('');
    if (!formData.title || !formData.amount || !formData.category_id || !formData.expense_date) {
      setStoreError('Please fill in all required fields');
      return;
    }

    try {
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: parseInt(formData.category_id, 10)
      };

      if (editingId) {
        const response = await updateExpense(editingId, expenseData);
        const expense = response.expense || response;
        updateExpenseInStore(editingId, expense);
        setMessage('Expense updated successfully');
      } else {
        const response = await createExpense(expenseData);
        const expense = response.expense || response;
        addExpense(expense);
        setMessage('Expense added successfully');
      }

      resetForm();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setStoreError(err.message || 'Failed to save expense');
    }
  };

  const handleEdit = (expense) => {
    setFormData({
      title: expense.title,
      amount: expense.amount,
      category_id: expense.category_id,
      expense_date: expense.expense_date.split('T')[0], // format for date input
      notes: expense.notes
    });
    setEditingId(expense.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await deleteExpense(id);
      removeExpense(id);
      setMessage('Expense deleted successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setStoreError(err.message || 'Failed to delete expense');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      amount: '',
      category_id: '',
      expense_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getFilteredAndSortedExpenses = () => {
    let filtered = expenses;

    if (filterCategory !== 'All') {
      filtered = filtered.filter((exp) => exp.category_name === filterCategory);
    }

    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.expense_date) - new Date(a.expense_date);
      } else if (sortBy === 'amount') {
        return b.amount - a.amount;
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

    return filtered;
  };

  const filteredExpenses = getFilteredAndSortedExpenses();
  const paginationData = {
    data: filteredExpenses.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    total: filteredExpenses.length,
    page: currentPage,
    pageSize,
    pages: Math.ceil(filteredExpenses.length / pageSize)
  };
  const totalAmount = filteredExpenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">Expenses</h1>
          <p className="text-gray-700">Track and manage branch expenses</p>
        </div>
        <div className="flex gap-3">
        <Button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          + Add Expense
        </Button>
        <Button
          onClick={() => setShowCategoryManager((prev) => !prev)}
          variant="secondary"
        >
          {showCategoryManager ? 'Hide Category Manager' : 'Manage Categories'}
        </Button>
      </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg">
          {message}
        </div>
      )}
      {(error || storeError) && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
          {error || storeError}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading expenses...</p>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Expenses</h3>
            <p className="text-2xl font-bold text-blue-600">
              PKR {Number(summary.totalExpenses || 0).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{summary.count} expenses</p>
          </div>
        </div>
      )}

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Edit Expense' : 'Add New Expense'}
        size="large"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Office supplies purchase"
              required
            />
            <FormInput
              label="Amount"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelectField
              label="Category"
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              options={categories
                .filter(cat => cat.is_active)
                .map(cat => ({ value: cat.id, label: cat.name }))}
              required
            />
            <FormInput
              label="Expense Date"
              name="expense_date"
              type="date"
              value={formData.expense_date}
              onChange={handleInputChange}
              required
            />
          </div>

          <FormInput
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Additional notes (optional)"
          />

          <div className="flex flex-wrap gap-2 justify-end pt-3 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="success">
              {editingId ? 'Update Expense' : 'Add Expense'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Filters and Sort */}
      <div className="bg-white p-4 rounded-lg shadow-md space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SearchableSelectField
            label="Filter by Category"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            options={[
              { value: 'All', label: 'All Categories' },
              ...categories.map(cat => ({ value: cat.name, label: cat.name }))
            ]}
          />
          <SearchableSelectField
            label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            options={[
              { value: 'date', label: 'Date (Newest First)' },
              { value: 'amount', label: 'Amount (Highest First)' },
              { value: 'title', label: 'Title (A-Z)' }
            ]}
          />
        </div>
        <div className="text-sm text-gray-600 pt-2 border-t">
          Showing {filteredExpenses.length} expense(s) • Total: <span className="font-semibold">PKR {totalAmount.toFixed(2)}</span>
        </div>
      </div>

      {showCategoryManager && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold">Category Management</h3>
              <p className="text-sm text-gray-600">Create, rename, activate, or remove expense categories for this branch.</p>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <FormInput
                label="New category"
                name="newCategory"
                value={newCategoryName}
                onChange={handleNewCategoryChange}
                placeholder="Enter category name"
              />
              <Button variant="primary" onClick={handleAddCategory}>Add</Button>
            </div>
          </div>

          {categoryMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
              {categoryMessage}
            </div>
          )}
          {categoryError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {categoryError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left text-gray-600">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {editingCategoryId === category.id ? (
                        <input
                          value={editingCategoryName}
                          onChange={handleCategoryNameChange}
                          className="w-full border border-gray-300 rounded-xl px-3 py-2"
                        />
                      ) : (
                        <span className="font-medium text-gray-900">{category.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${category.is_active ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {category.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-2">
                      {editingCategoryId === category.id ? (
                        <>
                          <Button variant="success" size="small" onClick={handleSaveCategoryEdit}>Save</Button>
                          <Button variant="secondary" size="small" onClick={handleCancelCategoryEdit}>Cancel</Button>
                        </>
                      ) : (
                        <div className='flex gap-2'>
                          <Button variant="secondary" size="small" onClick={() => handleEditCategoryStart(category)}>Rename</Button>
                          <Button variant="primary" size="small" onClick={() => handleToggleCategoryActive(category)}>
                            {category.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button variant="danger" size="small" onClick={() => handleDeleteCategory(category.id)}>Delete</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expenses Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {paginationData.total === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No expenses found</p>
          </div>
        ) : (
          <>
            <div className="h-[450px] overflow-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Notes</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginationData.data.map((expense) => (
                    <tr key={expense.id} className="border-b hover:bg-gray-50 transition">
                      <td className="px-6 py-3 text-sm text-gray-800">
                        {new Date(expense.expense_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-800 font-medium">
                        {expense.title}
                      </td>
                      <td className="px-6 py-3 text-sm">
                        <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {expense.category_name}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm font-semibold text-gray-800">
                        PKR {expense.amount}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600 truncate" title={expense.notes}>
                        {expense.notes || '-'}
                      </td>
                      <td className="px-6 py-3 text-sm space-x-2 flex">
                        <button
                          onClick={() => handleEdit(expense)}
                          className="text-blue-600 hover:text-blue-800 font-medium transition"
                        >
                          ✎ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600 hover:text-red-800 font-medium transition"
                        >
                          🗑 Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {paginationData.pages > 1 && (
              <div className="border-t p-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {paginationData.data.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, paginationData.total)} of {paginationData.total} expenses
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalItems={paginationData.total}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Category Breakdown */}
      {summary && Object.keys(summary.byCategory).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Expenses by Category</h3>
            <div className="space-y-3">
              {Object.entries(summary.byCategory)
                .sort((a, b) => b[1] - a[1])
                .map(([category, amount]) => (
                  <div key={category} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-gray-700">{category}</span>
                    <span className="font-semibold text-gray-900">PKR {(amount || 0).toFixed(2)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;